import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useHypoteseListe } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteser } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { useMoete, useMoeteSkriving, type DecisionMeeting, type DecisionMeetingItem, type MeetingRegistrationUpdate } from '../api/useMoeter.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useTilgang } from '../skall/tilgang.ts';
import { useSidetittel } from '../skall/tittel.ts';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseStatusNoekkel, tokenVar } from '../status/status.ts';
import { Tekstknapp, Varsel } from './deler.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { navnAv, statusNavn } from './lesning.ts';
import { aktiveRader, alleRader, fristKort, gjeldende, innsendere, kodeNavn, langDato, moeteNavn, navneliste, utfallOppforing, UTFALL } from './moeteLesning.ts';
import { aapneSkjermen, useMoeteKanal } from './moeteSynk.ts';
import { erHBehandler, HYPOTESER } from './rommet.ts';
import { kortDatoAv } from './setning.ts';
import { forUtfall, UtfallFelter, type Utfallsverdier } from './UtfallFelter.tsx';

/**
 * Kjøringen (H3.2, W5, W8, W8b): møtet på skjermen til den som registrerer, i et eget vindu i
 * panelet uten sidemeny. Agendaen til venstre med det som er registrert per rad; hypotesen i
 * kortform, utfallet (V P A I) registrert på beslutningstakerens vegne, eieren, svaret med
 * leietakerens mal, tallene — bare med «Menneskesatte tall» på, aldri en sum — og notatet, som er
 * hennes alene. Alt lagres straks på møteraden; ingen hypotese skifter status før beslutningstakeren
 * har bekreftet.
 *
 * Skjermen i rommet følger gjennom BroadcastChannel: kjøringen sier hvilken hypotese som vises, og at
 * et utfall er registrert. Uten nett lever skjermen videre; utfallene står lokalt og lagres med
 * «Prøv igjen» når nettet er tilbake.
 */
export function Kjoering({ moeteId, nr }: { moeteId: string; nr: number | null }) {
  const moete = useMoete(moeteId);
  const tilgang = useTilgang();
  const me = useMe();
  const m = moete.data;

  if (!tilgang.kjent || moete.isPending) {
    return (
      <main className="kj kj-melding" id="innhold">
        <p role="status">Henter møtet …</p>
      </main>
    );
  }
  if (!tilgang.kanSe(HYPOTESER) || moete.isError || !m) {
    return (
      <main className="kj kj-melding" id="innhold">
        <Varsel>Møtet kunne ikke hentes. Det kan være avlyst, eller ikke ditt å kjøre.</Varsel>
        <Link to="/h/moter" className="panel-knapp-sekundaer">
          Til Møter
        </Link>
      </main>
    );
  }
  // Kjøringen er registrars, og bare mens møtet går. Sendt eller bekreftet: til loggen.
  if (m.status !== 'planlagt' || !m.startedAt || m.registeredBy.id !== me.data?.id || !erHBehandler(tilgang)) return <Navigate to={`/h/moter/${moeteId}`} replace />;
  return <KjoeringFlate m={m} nr={nr} />;
}

function KjoeringFlate({ m, nr }: { m: DecisionMeeting; nr: number | null }) {
  const naviger = useNavigate();
  const config = useHypoteseConfig();
  const me = useMe();
  const skriv = useMoeteSkriving(m.id);
  const rader = useMemo(() => aktiveRader(m), [m]);
  const uttatt = useMemo(() => alleRader(m).filter((i) => i.state === 'tatt_ut'), [m]);
  const valgt = rader.find((i) => i.hypothesis.number === nr) ?? rader[0];
  const indeks = valgt ? rader.indexOf(valgt) : -1;
  const neste = indeks >= 0 ? rader[indeks + 1] : undefined;
  const forrige = indeks > 0 ? rader[indeks - 1] : undefined;
  const detaljer = useHypoteser(valgt ? [valgt.hypothesis.id] : []);
  const detalj = detaljer[0]?.data;
  const testetI = useHypoteseListe({ statusType: 'complete' }, valgt ? gjeldende(valgt)?.rejectReason === 'vurdert_for' : false);
  useSidetittel(`Kjøringen · ${moeteNavn(m)}`, me.data?.tenantName);

  // Det som er valgt her, men ikke lagret ennå (nett eller feil): står over tjenerens verdi.
  const [utkast, setUtkast] = useState<Record<string, Utfallsverdier>>({});
  const [notat, setNotat] = useState<Record<string, string>>({});
  const [feil, setFeil] = useState<{ hypoteseId: string; nummer?: number; nett: boolean; tekst?: string } | null>(null);
  const [avslutter, setAvslutter] = useState(false);
  const [skjermAapen, setSkjermAapen] = useState<boolean | null>(null);
  const [kvittering, setKvittering] = useState<string | null>(null);

  const send = useMoeteKanal(m.id, (msg) => {
    if (msg.type === 'her') {
      setSkjermAapen(true);
      if (valgt) send({ type: 'vis', hypoteseId: valgt.hypothesis.id });
    } else if (msg.type === 'borte') setSkjermAapen(false);
    else if (msg.type === 'vis') {
      const til = rader.find((i) => i.hypothesis.id === msg.hypoteseId);
      if (til && til !== valgt) void naviger(`/h/moter/${m.id}/kjor/${til.hypothesis.number}`, { replace: true });
    }
  });

  // Spør om skjermen er åpen; uten svar på halvannet sekund er den lukket (W8b).
  useEffect(() => {
    send({ type: 'ping' });
    const t = window.setTimeout(() => setSkjermAapen((a) => a ?? false), 1500);
    return () => window.clearTimeout(t);
  }, [send]);

  // Skjermen følger raden kjøringen står på.
  const valgtId = valgt?.hypothesis.id;
  useEffect(() => {
    if (valgtId) send({ type: 'vis', hypoteseId: valgtId });
  }, [valgtId, send]);

  const til = useCallback((i: DecisionMeetingItem | undefined) => (i ? void naviger(`/h/moter/${m.id}/kjor/${i.hypothesis.number}`) : undefined), [naviger, m.id]);

  const lagre = useCallback(
    async (rad: DecisionMeetingItem, body: MeetingRegistrationUpdate) => {
      const id = rad.hypothesis.id;
      try {
        await skriv.mutateAsync({ handling: 'registrer', hypoteseId: id, body });
        setFeil((f) => (f?.hypoteseId === id ? null : f));
        setUtkast((u) => {
          const rest = { ...u };
          delete rest[id];
          return rest;
        });
        send({ type: 'utfall' });
      } catch (e) {
        if (erApiFeil(e) && !erNettfeil(e) && e.status !== 0 && e.status < 500) {
          // Tjeneren sa nei (kappløp, ugyldig verdi): tjenerens verdi står, og grunnen sies.
          setUtkast((u) => {
            const rest = { ...u };
            delete rest[id];
            return rest;
          });
          setFeil({ hypoteseId: id, nummer: rad.hypothesis.number, nett: false, tekst: e.brukertekst });
          return;
        }
        setFeil({ hypoteseId: id, nummer: rad.hypothesis.number, nett: erNettfeil(e) });
      }
    },
    [skriv, send],
  );

  const verdierFor = (rad: DecisionMeetingItem): Utfallsverdier => {
    const r = rad.registered;
    const fra: Utfallsverdier = r
      ? {
          outcome: r.outcome,
          ownerId: r.owner?.id ?? null,
          revisitAt: r.revisitAt ?? null,
          rejectReason: r.rejectReason ?? null,
          relatedHypothesisId: r.relatedHypothesis?.id ?? null,
          response: r.response ? { text: r.response.text, ...(r.response.templateId ? { templateId: r.response.templateId } : {}) } : null,
          impact: r.scores?.impact ?? null,
          confidence: r.scores?.confidence ?? null,
          effort: r.scores?.effort ?? null,
        }
      : {};
    return { ...fra, ...(utkast[rad.hypothesis.id] ?? {}) };
  };

  const endre = useCallback(
    (rad: DecisionMeetingItem, e: Utfallsverdier) => {
      const id = rad.hypothesis.id;
      setUtkast((u) => ({ ...u, [id]: { ...(u[id] ?? {}), ...e } }));
      void lagre(rad, e as MeetingRegistrationUpdate);
    },
    [lagre],
  );

  // V P A I velger utfall, N går til notatet, ← → bytter rad. Aldri mens et felt har fokus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      if (avslutter || !valgt) return;
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (neste) til(neste);
        else setAvslutter(true);
        return;
      }
      if (maal && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      const k = UTFALL.find((u) => utfallOppforing(u).tast === e.key.toLowerCase());
      if (k) {
        e.preventDefault();
        if (verdierFor(valgt).outcome !== k) endre(valgt, forUtfall(k, { config: config.data, innsender: navnAv(valgt.hypothesis.submittedBy), foreslaattEier: valgt.proposedOwner }));
      } else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        document.getElementById('kj-notat')?.focus();
      } else if (e.key === 'ArrowRight' && neste) {
        e.preventDefault();
        til(neste);
      } else if (e.key === 'ArrowLeft' && forrige) {
        e.preventDefault();
        til(forrige);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!valgt) {
    return (
      <main className="kj kj-melding" id="innhold">
        <Varsel tone="info" rolle="status">
          Ingen hypotese står i Vurderes på agendaen lenger.
        </Varsel>
        <button type="button" className="panel-knapp" onClick={() => setAvslutter(true)}>
          Avslutt møtet …
        </button>
        {avslutter ? <AvsluttDialog m={m} onLukk={() => setAvslutter(false)} onSendt={() => void naviger(`/h/moter/${m.id}`)} /> : null}
      </main>
    );
  }

  const v = verdierFor(valgt);
  const h = valgt.hypothesis;
  const beslutter = navnAv(m.decider);
  const registrert = Boolean(v.outcome);
  const eier = v.ownerId ? config.data?.members.find((p) => p.id === v.ownerId) : undefined;
  const vet = registrert ? [...innsendere(h), ...(v.outcome === 'velg' && eier ? [navnAv(eier)] : [])] : [];
  const notatTekst = notat[h.id] ?? valgt.note ?? '';

  return (
    <div className="kj">
      <header className="kj-topp">
        <Link to={`/h/moter/${m.id}`} className="panel-knapp-lenke">
          <Ikon navn="chevron-back-outline" />
          Agendaen
        </Link>
        <span className="kj-topp-moete">{`${moeteNavn(m)} · ${beslutter} bestemmer · ${indeks + 1} av ${rader.length}`}</span>
        {skjermAapen ? (
          <span className="kj-skjerm-merke" role="status">
            <Ikon navn="tv-outline" />
            Vises på skjermen i rommet
          </span>
        ) : null}
        <button type="button" className="panel-knapp-lenke kj-avslutt" onClick={() => setAvslutter(true)}>
          Avslutt møtet …
        </button>
      </header>
      <div className="kj-kropp">
        <nav aria-label="Agenda" className="kj-agenda">
          <span className="kj-agenda-tittel">
            Agenda <span className="mu-hint">← → bytter</span>
          </span>
          <ol>
            {rader.map((i) => {
              const o = verdierFor(i);
              const naa = i === valgt;
              const u = o.outcome ? utfallOppforing(o.outcome) : null;
              const ownerNavn = o.ownerId ? navnAv(config.data?.members.find((p) => p.id === o.ownerId)) : null;
              const under = naa && !u ? 'Nå' : u ? [u.navn.replace(/ til …$/, ''), o.outcome === 'velg' && ownerNavn ? `${ownerNavn} eier` : null, o.outcome === 'parker' && o.revisitAt ? `til ${langDato(o.revisitAt)}` : null, o.outcome === 'avvis' ? kodeNavn(config.data, o.rejectReason ?? undefined) : null].filter(Boolean).join(' · ') : 'ikke registrert';
              return (
                <li key={i.hypothesis.id}>
                  <Link to={`/h/moter/${m.id}/kjor/${i.hypothesis.number}`} className={naa ? 'kj-agenda-rad kj-agenda-naa' : 'kj-agenda-rad'} aria-current={naa ? 'step' : undefined} data-testid={`kj-agenda-${i.hypothesis.number}`} style={u ? ({ '--kj-tone': tokenVar(u.fg) } as CSSProperties) : undefined}>
                    <Ikon navn={u ? u.ikon : naa ? 'hourglass-outline' : 'ellipse-outline'} className="kj-agenda-ikon" />
                    <span className="kj-agenda-tekst">
                      <span className="kj-agenda-navn">{i.hypothesis.title}</span>
                      <span className="kj-agenda-under">{under}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
          <p className="kj-agenda-fot" aria-hidden="true">
            <kbd>V</kbd> velg · <kbd>P</kbd> parker · <kbd>A</kbd> avvis · <kbd>I</kbd> ikke i dag · <kbd>N</kbd> notat
          </p>
        </nav>
        <main id="innhold" className="kj-hoved" aria-labelledby="kj-tittel">
          <div className="kj-rull">
            <div className="kj-innhold">
              <Meldinger m={m} uttatt={uttatt} feil={feil} skjermAapen={skjermAapen} kvittering={kvittering} onProevIgjen={() => {
                const u = utkast[feil?.hypoteseId ?? ''];
                const rad = rader.find((x) => x.hypothesis.id === feil?.hypoteseId);
                if (u && rad) void lagre(rad, u as MeetingRegistrationUpdate);
                else setFeil(null);
              }} onAapneSkjermen={() => {
                aapneSkjermen(m.id);
                setKvittering(null);
              }} />
              <p className="kj-meta">
                <span>{[`${nummerTekst(h.number)} · fra ${navneliste(innsendere(h))}${h.submittedAt ? ` ${kortDatoAv(h.submittedAt)}` : ''}`, fristKort(h) ? `frist ${fristKort(h)}` : null, detalj ? `${detalj.evidenceCount} innspill i grunnlaget` : null].filter(Boolean).join(' · ')}</span>
                <a href={`/h/registeret/${h.number}`} target="_blank" rel="noopener noreferrer" className="panel-knapp-lenke kj-aapne">
                  <Ikon navn="open-outline" />
                  {`Åpne ${nummerTekst(h.number)}`}
                </a>
              </p>
              <h1 id="kj-tittel" className="kj-tittel">
                {h.title}
              </h1>
              <p className="kj-naa">
                <HypotesePille noekkel={hypoteseStatusNoekkel(h.status, h.outcome)} navn={statusNavn(config.data, h) || 'Vurderes'} />
                {valgt.recommendation ? <span className="hb-dempet">{`${valgt.recommendation.by.id === me.data?.id ? 'Din anbefaling' : `Anbefaling fra ${navnAv(valgt.recommendation.by)}`}: ${valgt.recommendation.text}`}</span> : null}
              </p>
              {h.sentence ? <p className="kj-setning">{h.sentence}</p> : null}
              <UtfallFelter
                key={h.id}
                id={`kj-${h.number}`}
                etikett={`Utfall — registreres for ${beslutter}`}
                verdier={v}
                config={config.data}
                decider={m.decider}
                innsender={h.submittedBy}
                medlemmer={config.data?.members ?? []}
                tall={Boolean(config.data?.scoresEnabled)}
                testetI={testetI.data?.pages.flatMap((p) => p.items)}
                foreslaattEier={valgt.proposedOwner}
                onEndre={(e) => endre(valgt, e)}
              />
              <label className="panel-felt kj-notat">
                <span>
                  Notat <span className="mu-hint">bare du ser det</span>
                </span>
                <textarea
                  id="kj-notat"
                  rows={3}
                  maxLength={1000}
                  value={notatTekst}
                  onChange={(e) => setNotat((n) => ({ ...n, [h.id]: e.target.value }))}
                  onBlur={() => {
                    const ny = notat[h.id];
                    if (ny === undefined || ny === (valgt.note ?? '')) return;
                    void lagre(valgt, { note: ny });
                  }}
                />
              </label>
            </div>
          </div>
          <div className="h-fot kj-fot">
            <button type="button" className="panel-knapp h-primar" aria-keyshortcuts="Control+Enter" onClick={() => (neste ? til(neste) : setAvslutter(true))} data-testid="kj-neste">
              {neste ? `Neste: ${neste.hypothesis.title} →` : 'Avslutt møtet …'}
              <kbd>Ctrl ↵</kbd>
            </button>
            <span className="kj-fot-tekst">{registrert ? `Registrert for ${beslutter}. ${navneliste(vet)} får ikke beskjed før han har bekreftet.` : 'Ingen innsender får beskjed i møtet.'}</span>
          </div>
        </main>
      </div>
      {avslutter ? (
        <AvsluttDialog
          m={m}
          onLukk={() => setAvslutter(false)}
          onSendt={() => {
            send({ type: 'lukk' });
            void naviger(`/h/moter/${m.id}`, { state: { kvittering: `Lista er sendt til ${beslutter} i «Til deg».` } });
          }}
        />
      ) : null}
    </div>
  );
}

/** W8b: feil og delvis — ingen av dem røde. Det du valgte, står. */
function Meldinger({ m, uttatt, feil, skjermAapen, onProevIgjen, onAapneSkjermen }: { m: DecisionMeeting; uttatt: DecisionMeetingItem[]; feil: { hypoteseId: string; nummer?: number; nett: boolean; tekst?: string } | null; skjermAapen: boolean | null; kvittering: string | null; onProevIgjen: () => void; onAapneSkjermen: () => void }) {
  return (
    <div className="kj-meldinger">
      {feil?.nett ? (
        <div role="status" className="h-varsel h-varsel-info">
          <Ikon navn="cloud-offline-outline" className="h-varsel-ikon" />
          <span className="h-varsel-tekst">Ingen nett. Utfallene lagres når nettet er tilbake — skjermen i rommet virker som før.</span>
          <Tekstknapp onClick={onProevIgjen}>Prøv igjen</Tekstknapp>
        </div>
      ) : feil ? (
        <Varsel tone="info" handling={<Tekstknapp onClick={onProevIgjen}>Prøv igjen</Tekstknapp>}>
          {feil.tekst ?? `Utfallet for ${nummerTekst(feil.nummer)} ble ikke lagret. Det du valgte står.`}
        </Varsel>
      ) : null}
      {skjermAapen === false ? (
        <Varsel tone="warning" rolle="status" handling={<Tekstknapp onClick={onAapneSkjermen}>Åpne den igjen</Tekstknapp>}>
          Skjermen i rommet er lukket.
        </Varsel>
      ) : null}
      {uttatt.map((i) => (
        <Varsel key={i.hypothesis.id} tone="warning" rolle="status">
          {`${nummerTekst(i.hypothesis.number)} ble ${UTTATT[i.removedStatus ?? ''] ?? 'flyttet'}${hvemTok(i) ? ` av ${hvemTok(i)}` : ''} mens møtet pågikk. Den tas ut av agendaen.`}
        </Varsel>
      ))}
      <span hidden>{m.id}</span>
    </div>
  );
}

const UTTATT: Record<string, string> = { trukket: 'trukket', parkert: 'parkert', valgt: 'valgt', avvist: 'avvist', slaatt_sammen: 'slått sammen' };
/** Den som tok hypotesen ut: innsenderen trekker; ellers beslutningstakeren som står på den. */
const hvemTok = (i: DecisionMeetingItem): string | null => (i.removedStatus === 'trukket' ? navnAv(i.hypothesis.submittedBy) : i.hypothesis.decider ? navnAv(i.hypothesis.decider) : null);

/**
 * «Avslutt møtet» (W8): en dialog, fordi handlingen rammer andre — lista sendes. Radene uten utfall
 * står med navn og frist før hun trykker; setningen sier hvor lista går og hvem som ikke får svar ennå.
 */
export function AvsluttDialog({ m, onLukk, onSendt }: { m: DecisionMeeting; onLukk: () => void; onSendt: () => void }) {
  const skriv = useMoeteSkriving(m.id);
  const [feil, setFeil] = useState<string | null>(null);
  const rader = aktiveRader(m);
  const uten = rader.filter((i) => !gjeldende(i));
  const med = rader.filter((i) => gjeldende(i) && gjeldende(i)!.outcome !== 'ikke_i_dag');
  const beslutter = navnAv(m.decider);
  const venter = [...new Set(med.flatMap((i) => innsendere(i.hypothesis)))];
  const knapp = useRef<HTMLButtonElement>(null);

  async function avslutt() {
    setFeil(null);
    try {
      await skriv.mutateAsync({ handling: 'avslutt' });
      onSendt();
    } catch (e) {
      setFeil(erApiFeil(e) ? (erNettfeil(e) ? 'Ikke sendt — ingen nett. Prøv igjen når nettet er tilbake.' : e.brukertekst) : 'Noe gikk galt. Prøv igjen.');
    }
  }

  return (
    <Dialog tittelId="kj-avslutt-h" onLukk={onLukk} testid="kj-avslutt" fokusFørst={knapp}>
      <h2 id="kj-avslutt-h" className="panel-h2">
        Avslutt møtet
      </h2>
      <p className="panel-tekst">
        {uten.length === 0
          ? `Alle ${rader.length} har fått utfall.`
          : `${rader.length - uten.length} av ${rader.length} har fått utfall. ${uten.length === 1 ? 'Én settes' : `${uten.length} settes`} til «Ikke i dag» og blir stående i Vurderes med fristen sin:`}
      </p>
      {uten.length ? (
        <ul className="kj-ikke-i-dag">
          {uten.map((i) => (
            <li key={i.hypothesis.id}>
              <Ikon navn="play-skip-forward-outline" />
              <span>
                <b>{i.hypothesis.title}</b>
                <span className="mu-hint">{`${nummerTekst(i.hypothesis.number)}${fristKort(i.hypothesis) ? ` · frist ${fristKort(i.hypothesis)}` : ''}`}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="panel-tekst">{`Lista går til ${beslutter} i «Til deg».${venter.length ? ` ${navneliste(venter)} får ikke svar før han har bekreftet.` : ''}`}</p>
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger">
        <button ref={knapp} type="button" className="panel-knapp mo-knapp-ikon" aria-busy={skriv.isPending || undefined} onClick={skriv.isPending ? undefined : () => void avslutt()}>
          <Ikon navn="send-outline" />
          {`Avslutt og send til ${beslutter}`}
        </button>
        <Knapp label="Tilbake til møtet" kbd="Esc" lenke onClick={onLukk} />
      </div>
    </Dialog>
  );
}
